import { D, pipe } from "@mobily/ts-belt";
import {
  CreateObservationInput,
  MaterialGroup,
  PlantMaterialStatus,
} from "generated/graphqlSDK";
import { cleanFormData } from "src/utils/forms";
import { mapTagsToIds } from "src/utils/forms/map-tags-to-ids";
import { mapTaxonOptionToId } from "src/utils/forms/map-taxon-option-to-id";
import { TiptapDocument } from "src/utils/tiptap";
import {
  AccessionNumberFormat,
  type CreateAccession,
} from "../../../../generated/graphql";
import { convertDatesToISO } from "../../../utils/convert-dates-to-iso";
import {
  type IdentificationDetails,
  type MaterialReceivedOptionWithoutId,
} from "../components/accession-data-form/schema";
import { CountryOption } from "../components/accession-data-form/wild-collection-dialog";
import { MaterialImageSchema } from "../components/add-plant-material-form/material-image-schema";
import type {
  CollectionSiteLocationOption,
  PlantMaterialData,
} from "../components/add-plant-material-form/schema";
import type { CreateAccessionFormSchema } from "../components/schema";

export const mapCollectionSiteLocationOptionToId = <
  T extends { collectionSiteLocationOption: CollectionSiteLocationOption },
>({
  collectionSiteLocationOption,
  ...rest
}: T) => ({
  ...rest,
  collectionSiteLocationId: collectionSiteLocationOption.id,
});

export const mapMaterialReceivedOptionToCode = <
  T extends {
    materialTypeId?: MaterialReceivedOptionWithoutId | null | undefined;
  },
>({
  materialTypeId,
  ...rest
}: T) => ({
  ...rest,
  materialReceivedCode: materialTypeId?.code ?? null,
});

export const mapIdentificationDetails = <
  T extends {
    identificationDetails?: IdentificationDetails | null | undefined;
  },
>({
  identificationDetails,
  ...rest
}: T) =>
  ({
    ...rest,
    identifiedBy: identificationDetails?.identifiedBy ?? null,
    dateIdentified:
      //eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      identificationDetails?.dateIdentified?.toISOString() ?? null,
    identificationVerificationStatus:
      identificationDetails?.identificationVerificationStatus ?? null,
    identificationQualifier:
      identificationDetails?.identificationQualifier ?? null,
    identificationRemarks: identificationDetails?.identificationRemarks ?? null,
  }) satisfies Partial<CreateAccession>;

const processMaterialImages = <
  T extends {
    images?: Array<MaterialImageSchema> | null | undefined;
  },
>({
  images,
  ...rest
}: T) => ({
  ...rest,
  images: images?.map(convertDatesToISO),
});

const processInitialObservation = <
  T extends {
    materialGroup: MaterialGroup;
    status: PlantMaterialStatus;
    initialObservation?: CreateObservationInput | null | undefined;
  },
>({
  initialObservation,
  ...rest
}: T) =>
  initialObservation == null
    ? rest
    : {
        ...rest,
        initialObservation:
          rest.status !== PlantMaterialStatus.Absent &&
          rest.materialGroup !== MaterialGroup.Seed
            ? initialObservation
            : null,
      };

export const processPlantMaterialFormData = (data: PlantMaterialData) =>
  pipe(
    data,
    D.deleteKeys(["tempId", "materialQualifier"]),
    mapTagsToIds,
    mapCollectionSiteLocationOptionToId,
    processInitialObservation,
    processMaterialImages,
    convertDatesToISO,
    cleanFormData,
  );

export const mapPlantMaterials = <
  T extends {
    plantMaterials: Array<PlantMaterialData>;
  },
>({
  plantMaterials,
  ...rest
}: T) =>
  ({
    ...rest,
    plantMaterial: plantMaterials.map(processPlantMaterialFormData),
  }) satisfies Partial<CreateAccession>;

export const mapFormPlantMaterialLocation = (
  plantMaterial: CreateAccessionFormSchema["plantMaterials"][number],
) => {
  const { collectionSiteLocationOption, tags, ...rest } = plantMaterial;
  return {
    ...rest,
    collectionSiteLocationId: collectionSiteLocationOption.id,
    tags: tags.map(({ id }) => id),
  };
};

export const mapCountryOptionToId = <
  T extends { countryOption?: CountryOption | null | undefined },
>({
  countryOption,
  ...rest
}: T) =>
  ({
    ...rest,
    collectionCountryCode: countryOption?.codeId ?? null,
  }) satisfies Partial<CreateAccession>;

export const mapFlexibleAccessionInputs = <
  T extends {
    generateAccessionNumber: boolean;
    accessionNumberFormat?: AccessionNumberFormat | null;
    exogenousAccessionNumber?: string | null;
    accessionNumberPrefix?: number | null;
    accessionNumberSuffix?: number | null;
  },
>({
  generateAccessionNumber,
  accessionNumberFormat,
  accessionNumberPrefix,
  accessionNumberSuffix,
  exogenousAccessionNumber,
  ...rest
}: T) => {
  if (generateAccessionNumber) {
    return {
      ...rest,
      accessionNumberFormat: AccessionNumberFormat.PrefixSuffix,
      accessionNumberSuffix: null, //Needs to be explicity set to null to trigger auto-generation in the api
    };
  }

  if (accessionNumberFormat === AccessionNumberFormat.Exogenous) {
    return {
      ...rest,
      accessionNumberFormat,
      exogenousAccessionNumber,
    } satisfies Partial<CreateAccession>;
  }

  return {
    ...rest,
    accessionNumberFormat,
    accessionNumberPrefix,
    accessionNumberSuffix,
  } satisfies Partial<CreateAccession>;
};

export const mapNotesData = <
  T extends { notes?: TiptapDocument | null | undefined },
>({
  notes,
  ...rest
}: T) =>
  ({
    ...rest,
    notesData: notes,
  }) satisfies Partial<CreateAccession>;

export const processAccessionFormData = (data: CreateAccessionFormSchema) =>
  pipe(
    data,
    mapTagsToIds,
    mapIdentificationDetails,
    mapTaxonOptionToId,
    ({ materialTypeId, notes, ...rest }) => ({
      ...rest,
      ...mapNotesData({ notes }),
      ...mapMaterialReceivedOptionToCode({ materialTypeId }),
    }),
    ({ wildCollection, ...rest }) => ({
      ...rest,
      ...(rest.provenanceCategoryCode === "W" ||
      rest.provenanceCategoryCode === "Z"
        ? wildCollection
        : {}),
    }),
    mapCountryOptionToId,
    mapFlexibleAccessionInputs,
    mapPlantMaterials,
    cleanFormData,
  );
